import { z } from 'zod';
import { 
  campaignVariantSchema, 
  defaultCampaignVariants, 
  type CampaignVariant 
} from '../types/campaign';
import { logger } from '../utils/logger';

export class CampaignVariantService {
  private static instance: CampaignVariantService;
  private currentVariant: CampaignVariant | null = null;
  private initialized: boolean = false;

  private constructor() {}

  public static getInstance(): CampaignVariantService {
    if (!CampaignVariantService.instance) {
      CampaignVariantService.instance = new CampaignVariantService();
    }
    return CampaignVariantService.instance;
  }

  /**
   * Initialize campaign variant from URL parameters
   */
  public initializeFromUrl(): CampaignVariant {
    if (this.initialized && this.currentVariant) {
      return this.currentVariant;
    }

    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for specific variant parameter
    const variantId = urlParams.get('variant') || urlParams.get('campaign_variant');
    
    // Check for offer-specific parameters
    const offerCode = urlParams.get('offer_code') || urlParams.get('code');
    const firstName = urlParams.get('first_name') || urlParams.get('fname');
    const offerAmount = urlParams.get('offer_amount') || urlParams.get('amount');
    const offerType = urlParams.get('offer_type') || urlParams.get('type');
    
    // Check for channel parameters
    const channel = urlParams.get('channel');
    const source = urlParams.get('utm_source') || urlParams.get('source');
    const medium = urlParams.get('utm_medium') || urlParams.get('medium');
    const campaign = urlParams.get('utm_campaign') || urlParams.get('campaign');

    let baseVariant: CampaignVariant;

    // Determine base variant
    if (variantId && defaultCampaignVariants[variantId]) {
      baseVariant = { ...defaultCampaignVariants[variantId] };
    } else if (offerCode || firstName || offerAmount) {
      // If personalization parameters exist, use personalized variant
      baseVariant = { ...defaultCampaignVariants.personalized };
    } else if (offerType && defaultCampaignVariants[offerType]) {
      // If offer type matches a predefined variant
      baseVariant = { ...defaultCampaignVariants[offerType] };
    } else {
      // Default variant
      baseVariant = { ...defaultCampaignVariants.default };
    }

    // Apply personalizations
    const personalizedVariant: CampaignVariant = {
      ...baseVariant,
      ...(offerCode && { offerCode }),
      ...(firstName && { firstName }),
      ...(offerAmount && { offerAmount: parseInt(offerAmount, 10) }),
      ...(offerType && { offerType: offerType as any }),
      ...(channel && { channel }),
      ...(source && { source }),
      ...(medium && { medium }),
      ...(campaign && { campaign })
    };

    // Set default loan amount from offer amount if provided
    if (personalizedVariant.offerAmount) {
      personalizedVariant.defaultLoanAmount = personalizedVariant.offerAmount;
    }

    // Update headlines for personalized variants
    if (personalizedVariant.firstName) {
      if (personalizedVariant.offerAmount) {
        personalizedVariant.headline = `Hi ${personalizedVariant.firstName}, you're Eligible for up to ${this.formatCurrency(personalizedVariant.offerAmount)}`;
      } else {
        personalizedVariant.headline = `Hi ${personalizedVariant.firstName}, you're Eligible for a Personal Loan`;
      }
    }

    // Validate the final variant
    try {
      this.currentVariant = campaignVariantSchema.parse(personalizedVariant);
      this.initialized = true;
      
      logger.info('Campaign variant initialized', {
        variantId: this.currentVariant.id,
        personalized: !!(firstName || offerCode || offerAmount),
        source: this.currentVariant.source,
        channel: this.currentVariant.channel
      }, 'CampaignVariantService');

      return this.currentVariant;
    } catch (error) {
      logger.error('Invalid campaign variant configuration, falling back to default', error, 'CampaignVariantService');
      this.currentVariant = defaultCampaignVariants.default;
      this.initialized = true;
      return this.currentVariant;
    }
  }

  /**
   * Get current campaign variant
   */
  public getCurrentVariant(): CampaignVariant {
    if (!this.initialized) {
      return this.initializeFromUrl();
    }
    return this.currentVariant || defaultCampaignVariants.default;
  }

  /**
   * Check if variant has personalization
   */
  public isPersonalized(): boolean {
    const variant = this.getCurrentVariant();
    return !!(variant.firstName || variant.offerCode || variant.offerAmount);
  }

  /**
   * Get personalized headline
   */
  public getHeadline(): string {
    return this.getCurrentVariant().headline || defaultCampaignVariants.default.headline!;
  }

  /**
   * Get personalized subheadline
   */
  public getSubheadline(): string {
    return this.getCurrentVariant().subheadline || defaultCampaignVariants.default.subheadline!;
  }

  /**
   * Get personalized CTA text
   */
  public getCtaText(): string {
    return this.getCurrentVariant().ctaText || defaultCampaignVariants.default.ctaText!;
  }

  /**
   * Get default loan amount
   */
  public getDefaultLoanAmount(): number {
    return this.getCurrentVariant().defaultLoanAmount || defaultCampaignVariants.default.defaultLoanAmount!;
  }

  /**
   * Get default loan purpose
   */
  public getDefaultLoanPurpose(): string | undefined {
    return this.getCurrentVariant().defaultLoanPurpose;
  }

  /**
   * Get hero image URL
   */
  public getHeroImage(): string | undefined {
    return this.getCurrentVariant().heroImage;
  }

  /**
   * Get variant analytics data
   */
  public getAnalyticsData(): Record<string, string> {
    const variant = this.getCurrentVariant();
    return {
      variant_id: variant.id,
      variant_name: variant.name,
      ...(variant.channel && { channel: variant.channel }),
      ...(variant.source && { source: variant.source }),
      ...(variant.medium && { medium: variant.medium }),
      ...(variant.campaign && { campaign: variant.campaign }),
      ...(variant.offerType && { offer_type: variant.offerType }),
      personalized: this.isPersonalized().toString()
    };
  }

  /**
   * Reset the service (useful for testing)
   */
  public reset(): void {
    this.currentVariant = null;
    this.initialized = false;
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }
}

export const campaignVariantService = CampaignVariantService.getInstance();